import React, { useEffect, useMemo, useState } from 'react';
import { Select, Checkbox, Divider, Space, Tag, FormInstance } from 'antd';
import { CustomButton, CustomShipmentText } from '@/components';
import TruncateWithTooltip from '@/components/TruncateWithTooltip';
import { ChevronDownSmallIcon } from '@/assets/icons/icons';
import type { TeamGroup } from '@/apis/teamGroups';
import {
  groupMatchesSearch,
  groupSelectionState,
  isGroupValue,
  groupIdFromValue,
  teamPickerSectionLabels,
  toggleGroupSelection,
  toGroupValue,
} from '@/common/utils/teamGroups';

const { Option, OptGroup } = Select;

const areSameIds = (
  a: (string | number | undefined)[],
  b: (string | number | undefined)[]
) =>
  a.length === b.length &&
  a.every((value, index) => String(value) === String(b[index]));

type ListItem = {
  id?: number | string;
  courier_id?: number | string;
  label?: string;
  name?: string;
  courier_name?: string;
  address?: string;
};

interface ShipmentFilterApplySelectorProps {
  list: ListItem[];
  open: boolean;
  titleText?: string;
  background?: string;
  border?: string;
  color?: string;
  handleApplyFilters: (
    selected: (string | number)[],
    isAllSelected?: boolean
  ) => void;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isParernt?: boolean;
  applyType?: (string | number)[];
  applyCourierId?: boolean;
  patchedArray?: (string | number)[];
  patchedIsAll?: boolean;
  form?: FormInstance;
  formName?: string;
  hasMore?: boolean;
  loading?: boolean;
  onLoadMore?: () => void;
  disabled?: boolean;
  /** Team filter only: groups shown above the teams; picking one selects its teams. */
  teamGroups?: TeamGroup[];
}

const ShipmentFilterApplySelector: React.FC<
  ShipmentFilterApplySelectorProps
> = ({
  list,
  open,
  titleText,
  background,
  border,
  color,
  handleApplyFilters,
  setOpen,
  isParernt,
  applyType,
  applyCourierId = false,
  patchedArray = [],
  patchedIsAll = false,
  hasMore = false,
  loading = false,
  onLoadMore,
  disabled = false,
  teamGroups = [],
}) => {
  const sectionLabels = teamPickerSectionLabels();
  const [selectedOption, setSelectedOption] = useState<(string | number)[]>([]);
  const [appliedOptions, setAppliedOptions] = useState<(string | number)[]>([]);
  const [isAllSelectedValues, setIsAllSelectedValues] = useState(false);
  // tracks whether the last *applied* state was "all selected"
  const [appliedIsAll, setAppliedIsAll] = useState(false);

  const uniqueList: ListItem[] = useMemo(
    () =>
      list?.length > 0
        ? list.filter(
            (item, index, self) =>
              index ===
              self.findIndex(
                (t) =>
                  (applyCourierId ? t.courier_id : t.id) ===
                  (applyCourierId ? item.courier_id : item.id)
              )
          )
        : [],
    [applyCourierId, list]
  );

  const allIds: (string | number)[] = useMemo(
    () =>
      uniqueList.map(
        (item) =>
          (applyCourierId ? item.courier_id : item.id) as string | number
      ),
    [applyCourierId, uniqueList]
  );

  // When new items load via load-more and ALL is currently selected,
  // keep selectedOption in sync with the growing list.
  // Also handles the initial async list load when patchedIsAll=true.
  useEffect(() => {
    if (isAllSelectedValues && allIds.length > 0) {
      setSelectedOption((prev) => (areSameIds(prev, allIds) ? prev : allIds));
      // Keep appliedOptions fresh so the "All" tag keeps rendering
      if (appliedIsAll) {
        setAppliedOptions((prev) => (areSameIds(prev, allIds) ? prev : allIds));
      }
    }
  }, [allIds, appliedIsAll, isAllSelectedValues]);

  // Dedicated handler for the ALL option toggle
  const handleAllToggle = (): void => {
    if (isAllSelectedValues) {
      // Uncheck ALL → clear
      setIsAllSelectedValues(false);
      setSelectedOption([]);
    } else {
      // Check ALL → store all current IDs for display + checkbox state
      setIsAllSelectedValues(true);
      setSelectedOption(allIds);
    }
  };

  // Dedicated handler for individual item selections
  const handleItemChange = (values: (string | number)[]): void => {
    const filtered = values.filter((val) => val !== 'ALL');
    setIsAllSelectedValues(false);
    setSelectedOption(filtered);
  };

  // Routes to dedicated handler — ALL and individual items are fully separate
  const handleChange = (values: (string | number)[]): void => {
    // A group row is never kept as a value: it toggles its teams instead.
    const groupValue = values.find(isGroupValue);
    if (groupValue) {
      const group = teamGroups.find(
        (g) => g.id === groupIdFromValue(groupValue)
      );
      const current = isAllSelectedValues ? allIds : selectedOption;
      setSelectedOption(group ? toggleGroupSelection(group, current) : current);
      setIsAllSelectedValues(false);
      return;
    }
    if (values.includes('ALL')) {
      handleAllToggle();
    } else {
      handleItemChange(values);
    }
  };

  const handleReset = (): void => {
    setSelectedOption([]);
    setAppliedOptions([]);
    setIsAllSelectedValues(false);
    setAppliedIsAll(false);
    setOpen(false);
    handleApplyFilters([]);
  };

  const handleDropdownVisibleChange = (visible: boolean): void => {
    // When reopening, restore to last applied state.
    // If "all" was applied, always use current allIds (list may have grown via load-more)
    setSelectedOption(appliedIsAll ? allIds : appliedOptions);
    setIsAllSelectedValues(appliedIsAll);
    setOpen(visible);
  };

  const handleScroll = (e: React.UIEvent<HTMLElement>): void => {
    const target = e.currentTarget;
    const { scrollTop, clientHeight, scrollHeight } = target;
    if (
      scrollHeight - scrollTop <= clientHeight * 1.5 &&
      hasMore &&
      !loading &&
      onLoadMore
    ) {
      onLoadMore();
    }
  };

  useEffect(() => {
    if (patchedArray.length === 0 && !patchedIsAll) return;

    if (patchedIsAll) {
      // Saved data has "all selected" — restore flag immediately.
      // selectedOption/appliedOptions will be filled once list loads (uniqueList.length effect).
      setIsAllSelectedValues(true);
      setAppliedIsAll(true);
      if (allIds.length > 0) {
        setSelectedOption(allIds);
        setAppliedOptions(allIds);
      }
    } else {
      setSelectedOption(patchedArray);
      setAppliedOptions(patchedArray);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchedIsAll]);

  const handleApply = (values: (string | number)[]): void => {
    // appliedOptions keeps display values (all IDs) so the "All" tag renders
    setAppliedOptions(isAllSelectedValues ? allIds : values);
    setAppliedIsAll(isAllSelectedValues);
    // Parent selector (isParernt=true): send actual type IDs so applyType gets all values
    // Filter selectors (isParernt=false): send [] as the "select all" signal to the API
    const toSubmit = isAllSelectedValues ? (isParernt ? allIds : []) : values;
    handleApplyFilters(toSubmit, isAllSelectedValues);
  };

  useEffect(() => {
    if (isParernt && applyType) {
      setSelectedOption(applyType);
      setAppliedOptions(applyType);
    }
  }, [applyType, isParernt]);

  const isAllSelected = isAllSelectedValues;

  return (
    <>
      <Select
        mode='multiple'
        value={selectedOption}
        onChange={handleChange}
        open={open}
        onOpenChange={handleDropdownVisibleChange}
        disabled={disabled}
        onPopupScroll={handleScroll}
        placeholder='Select...'
        className='custom-dd-style placeholder-left'
        optionLabelProp='label'
        prefix={null}
        suffixIcon={
          <span
            // eslint-disable-next-line react/forbid-dom-props -- rotates with the open state
            style={{
              display: 'inline-flex',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          >
            <ChevronDownSmallIcon size={16} color='#5E5E5E' />
          </span>
        }
        filterOption={(input, option) => {
          const search = input.toLowerCase();
          if (isGroupValue(option?.value)) {
            const groupValue = option.value;
            const group = teamGroups.find(
              (g) => g.id === groupIdFromValue(groupValue)
            );
            return (
              !!group &&
              groupMatchesSearch(
                group,
                input,
                (id) => uniqueList.find((i) => String(i.id) === id)?.name
              )
            );
          }
          const item = uniqueList.find(
            (i) => (applyCourierId ? i.courier_id : i.id) === option?.value
          );
          if (!item) return false;
          // Check all possible label fields — label covers parent selector (NotificationApplyOptions)
          const label = (item.label || '').toLowerCase();
          const name = (item.name || '').toLowerCase();
          const courierName = (item.courier_name || '').toLowerCase();
          const address = (item.address || '').toLowerCase();
          return (
            label.includes(search) ||
            name.includes(search) ||
            courierName.includes(search) ||
            address.includes(search)
          );
        }}
        tagRender={({
          label,
          value,
        }: {
          label: React.ReactNode;
          value: string | number;
        }) => {
          const onPreventMouseDown = (
            event: React.MouseEvent<HTMLSpanElement>
          ): void => {
            event.preventDefault();
            event.stopPropagation();
          };
          // Ant Design may pass the raw value as label when option metadata isn't cached.
          // Fall back to looking up the display text from the list directly.
          const matchedItem = uniqueList.find(
            (i) => (applyCourierId ? i.courier_id : i.id) === value
          );
          const resolvedLabel =
            typeof label === 'string' && label !== String(value)
              ? label
              : matchedItem?.label ||
                matchedItem?.name ||
                matchedItem?.courier_name ||
                String(value);
          if (appliedOptions?.length > 0 || appliedIsAll) {
            if (isAllSelected) {
              if (value === allIds[0]) {
                return (
                  <Tag
                    className='notification-filter-tag'
                    closable={!isParernt && !disabled}
                    onMouseDown={onPreventMouseDown}
                    onClose={handleReset}
                    style={{
                      background: background || 'var(--surface-container)',
                      color: color || 'var(--text-secondary)',
                      border: border || '1px solid var(--gray-300)',
                    }}
                  >
                    <span>All</span>
                  </Tag>
                );
              }
              // One "All" tag stands in for every selected value.
              return <></>;
            }

            return (
              <Tag
                className='notification-filter-tag'
                closable={!isParernt && !disabled}
                onMouseDown={onPreventMouseDown}
                onClose={() => {
                  const newSelected = selectedOption?.filter(
                    (x) => x !== value
                  );
                  setSelectedOption(newSelected);
                  setAppliedOptions(newSelected);
                  handleApplyFilters(newSelected);
                }}
                style={{
                  background: background || 'var(--surface-container)',
                  color: color || 'var(--text-secondary)',
                  border: border || '1px solid var(--gray-300)',
                }}
              >
                <TruncateWithTooltip
                  text={resolvedLabel}
                  max={25}
                  render={(t) => (
                    <span className='w-fit'>
                      <span>{t}</span>
                    </span>
                  )}
                />
              </Tag>
            );
          }
          // Nothing applied yet: show no tags for the draft selection.
          return <></>;
        }}
        dropdownRender={(menu) => (
          <>
            {titleText && (
              <CustomShipmentText
                text={titleText}
                color='var(--text-secondary)'
                fontWeight={600}
                style={{ margin: '8px 26px 4px' }}
              />
            )}
            {menu}
            <Divider className='notification-divider' style={{ margin: '0' }} />
            <Space
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '8px 8px 4px',
              }}
            >
              <CustomButton
                text='Reset'
                onClick={() => handleReset()}
                width='auto'
                height='32px'
                bgColor='transparent'
                color='var(--text-primary)'
                fontSize='12px'
                fontWeight={600}
                borderRadius='0px'
                boxShadow='none'
              />
              <CustomButton
                text='Apply'
                onClick={() => handleApply(selectedOption)}
                width='62px'
                height='32px'
                bgColor='#000'
                color='#fff'
                fontSize='12px'
                fontWeight={500}
                borderRadius='8px'
                boxShadow='0px 4px 16px 0px rgba(0, 0, 0, 0.27)'
              />
            </Space>
          </>
        )}
      >
        {uniqueList.length > 0 && (
          <>
            <Option key='all' value='ALL' label='All'>
              <Checkbox
                className='notification-filter-checkbox'
                checked={isAllSelected}
              >
                All
              </Checkbox>
            </Option>

            {teamGroups.length > 0 && (
              <OptGroup key='team-groups' label={sectionLabels.groups}>
                {teamGroups.map((group) => {
                  const state = groupSelectionState(
                    group,
                    isAllSelectedValues ? allIds : selectedOption
                  );
                  return (
                    <Option
                      key={toGroupValue(group.id)}
                      value={toGroupValue(group.id)}
                      label={group.name}
                    >
                      <Checkbox
                        className='notification-filter-checkbox'
                        checked={state.checked}
                        indeterminate={state.indeterminate}
                      >
                        <TruncateWithTooltip
                          text={group.name}
                          max={25}
                          // The limit applies to the name; the count always shows.
                          render={(t) => (
                            <span className='w-fit'>
                              <span>{`${t} (${group.team_ids.length})`}</span>
                            </span>
                          )}
                        />
                      </Checkbox>
                    </Option>
                  );
                })}
              </OptGroup>
            )}
            {teamGroups.length > 0 ? (
              <OptGroup key='teams' label={sectionLabels.teams}>
                {uniqueList.map((item) => (
                  <Option
                    key={applyCourierId ? item.courier_id : item.id}
                    value={applyCourierId ? item.courier_id : item.id}
                    label={item.label || item.name || item.courier_name}
                  >
                    <Checkbox
                      className='notification-filter-checkbox'
                      checked={
                        isAllSelectedValues ||
                        selectedOption.some(
                          (selected) =>
                            selected ===
                            (applyCourierId ? item.courier_id : item.id)
                        )
                      }
                    >
                      <TruncateWithTooltip
                        text={String(
                          item.label || item.name || item.courier_name
                        )}
                        max={25}
                        render={(t) => (
                          <span className='w-fit'>
                            <span>{t}</span>
                          </span>
                        )}
                      />
                    </Checkbox>
                  </Option>
                ))}
              </OptGroup>
            ) : (
              uniqueList.map((item) => (
                <Option
                  key={applyCourierId ? item.courier_id : item.id}
                  value={applyCourierId ? item.courier_id : item.id}
                  label={item.label || item.name || item.courier_name}
                >
                  <Checkbox
                    className='notification-filter-checkbox'
                    checked={
                      isAllSelectedValues ||
                      selectedOption.some(
                        (selected) =>
                          selected ===
                          (applyCourierId ? item.courier_id : item.id)
                      )
                    }
                  >
                    <TruncateWithTooltip
                      text={String(
                        item.label || item.name || item.courier_name
                      )}
                      max={25}
                      render={(t) => (
                        <span className='w-fit'>
                          <span>{t}</span>
                        </span>
                      )}
                    />
                  </Checkbox>
                </Option>
              ))
            )}
          </>
        )}
      </Select>
    </>
  );
};

export default ShipmentFilterApplySelector;
